"use client";

import { useRef, useState } from "react";

/**
 * In-browser 8D audio converter.
 *
 * Everything runs locally via OfflineAudioContext — the file never leaves
 * the device. The DSP mirrors the 8Dfy extension engine's verified chain:
 * lowshelf bass → HRTF panner on an orbit → convolution reverb (wet mix) →
 * makeup gain → brickwall-ish limiter (threshold -1 dB / knee 0 / ratio 20,
 * the config measured clip-free in the extension) → WAV.
 */

const MAX_MINUTES = 20;

function describe8DMovement(value: number) {
  if (value <= 0.05) return "Centered";
  if (value < 0.35) return "Subtle";
  if (value < 0.7) return "Balanced";
  if (value < 0.9) return "Wide";
  return "Full orbit";
}

type Phase = "idle" | "decoding" | "rendering" | "done" | "error";

function makeReverbIR(ctx: OfflineAudioContext, seconds = 1.6, rt60 = 1.1) {
  const sr = ctx.sampleRate;
  const len = Math.floor(seconds * sr);
  const ir = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    let lp = 0,
      lp2 = 0; // two cascaded poles = 12 dB/oct high damping
    for (let i = 0; i < len; i++) {
      const t = i / sr;
      const env = Math.exp((-6.91 * t) / rt60);
      const fc = 10000 * Math.pow(2000 / 10000, t / seconds);
      const a = Math.exp((-2 * Math.PI * fc) / sr);
      lp = (1 - a) * (Math.random() * 2 - 1) + a * lp;
      lp2 = (1 - a) * lp + a * lp2;
      d[i] = lp2 * env * 0.6;
    }
  }
  return ir;
}

function encodeWav(rendered: AudioBuffer): Blob {
  const numCh = 2;
  const sr = rendered.sampleRate;
  const n = rendered.length;
  const data = new ArrayBuffer(44 + n * numCh * 2);
  const v = new DataView(data);
  const wstr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  wstr(0, "RIFF");
  v.setUint32(4, 36 + n * numCh * 2, true);
  wstr(8, "WAVE");
  wstr(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, numCh, true);
  v.setUint32(24, sr, true);
  v.setUint32(28, sr * numCh * 2, true);
  v.setUint16(32, numCh * 2, true);
  v.setUint16(34, 16, true);
  wstr(36, "data");
  v.setUint32(40, n * numCh * 2, true);
  const L = rendered.getChannelData(0);
  const R = rendered.numberOfChannels > 1 ? rendered.getChannelData(1) : L;
  let p = 44;
  for (let i = 0; i < n; i++) {
    const l = Math.max(-1, Math.min(1, L[i]));
    const r = Math.max(-1, Math.min(1, R[i]));
    v.setInt16(p, l < 0 ? l * 0x8000 : l * 0x7fff, true);
    p += 2;
    v.setInt16(p, r < 0 ? r * 0x8000 : r * 0x7fff, true);
    p += 2;
  }
  return new Blob([data], { type: "audio/wav" });
}

async function convertTo8D(
  file: File,
  opts: { speed: number; intensity: number; bass: number; reverb: number },
  onPhase: (p: Phase) => void
): Promise<Blob> {
  onPhase("decoding");
  const raw = await file.arrayBuffer();
  // A throwaway context only for decoding (Safari needs a live ctx).
  const probe = new AudioContext();
  const src = await probe.decodeAudioData(raw).finally(() => probe.close());
  if (src.duration > MAX_MINUTES * 60) {
    throw new Error(`File is longer than ${MAX_MINUTES} minutes — trim it first.`);
  }

  onPhase("rendering");
  const sr = src.sampleRate;
  const ctx = new OfflineAudioContext(2, src.length, sr);

  const source = ctx.createBufferSource();
  source.buffer = src;

  const bassShelf = ctx.createBiquadFilter();
  bassShelf.type = "lowshelf";
  bassShelf.frequency.value = 80;
  bassShelf.gain.value = opts.bass * 6; // 0..6 dB

  const panner = ctx.createPanner();
  panner.panningModel = "HRTF";
  panner.distanceModel = "inverse";
  panner.refDistance = 1;

  // Orbit path scheduled over the whole file: 32 control points per second
  // is far above the ~0.5 Hz orbit rate, so the motion is smooth.
  const R = 0.5 + opts.intensity * 1.0; // 0.5..1.5 orbit radius
  const pts = Math.max(2, Math.ceil(src.duration * 32));
  const xs = new Float32Array(pts);
  const zs = new Float32Array(pts);
  for (let i = 0; i < pts; i++) {
    const t = (i / (pts - 1)) * src.duration;
    const a = 2 * Math.PI * opts.speed * t;
    xs[i] = Math.cos(a) * R;
    zs[i] = Math.sin(a) * R;
  }
  panner.positionX.setValueCurveAtTime(xs, 0, src.duration);
  panner.positionZ.setValueCurveAtTime(zs, 0, src.duration);

  const convolver = ctx.createConvolver();
  convolver.buffer = makeReverbIR(ctx);
  const wet = ctx.createGain();
  wet.gain.value = opts.reverb * 0.35; // 0..0.35 wet
  const dry = ctx.createGain();
  dry.gain.value = 1;

  // HRTF costs ~-5.8 dB average level (measured in the extension) — make it
  // up before the limiter so output loudness matches the source.
  const makeup = ctx.createGain();
  makeup.gain.value = 1.94;

  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -1;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.25;

  source.connect(bassShelf);
  bassShelf.connect(panner);
  panner.connect(dry);
  panner.connect(convolver);
  convolver.connect(wet);
  dry.connect(makeup);
  wet.connect(makeup);
  makeup.connect(limiter);
  limiter.connect(ctx.destination);

  source.start();
  const rendered = await ctx.startRendering();
  return encodeWav(rendered);
}

export default function Converter() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState("");
  const [outUrl, setOutUrl] = useState("");
  const [outName, setOutName] = useState("");
  const [fileName, setFileName] = useState("");
  const [speed, setSpeed] = useState(0.15);
  const [intensity, setIntensity] = useState(1);
  const [bass, setBass] = useState(0.4);
  const [reverb, setReverb] = useState(0.25);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = phase === "decoding" || phase === "rendering";

  async function handleFile(file: File | undefined | null) {
    if (!file || busy) return;
    setError("");
    setFileName(file.name);
    if (outUrl) URL.revokeObjectURL(outUrl);
    setOutUrl("");
    try {
      const blob = await convertTo8D(file, { speed, intensity, bass, reverb }, setPhase);
      const base = file.name.replace(/\.[^.]+$/, "") || "track";
      setOutName(`${base}-8d.wav`);
      setOutUrl(URL.createObjectURL(blob));
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read that file — try an MP3, WAV or M4A.");
      setPhase("error");
    }
  }

  const slider = (
    label: string,
    value: number,
    set: (v: number) => void,
    min: number,
    max: number,
    fmt: (v: number) => string
  ) => (
    <label className="block">
      <span className="flex justify-between text-xs text-white/50 mb-1.5">
        <span>{label}</span>
        <span className="font-bold text-white/80">{fmt(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={0.01}
        value={value}
        disabled={busy}
        onChange={(e) => set(parseFloat(e.target.value))}
        className="w-full accent-[#FF3366]"
      />
    </label>
  );

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      {/* Drop zone */}
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className="w-full rounded-xl border-2 border-dashed border-white/15 hover:border-[#FF3366]/60 transition-colors px-6 py-10 text-center cursor-pointer disabled:opacity-60"
      >
        <div className="text-3xl mb-2" aria-hidden>
          🎧
        </div>
        <div className="font-bold text-white/90">
          {busy
            ? phase === "decoding"
              ? "Reading your file…"
              : "Rendering 8D audio…"
            : fileName || "Drop a song here, or click to choose"}
        </div>
        <div className="text-xs text-white/55 mt-1.5">
          MP3, WAV, M4A, OGG, FLAC · up to {MAX_MINUTES} min · never uploaded — converts on your device
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-6">
        {slider("Rotation speed", speed, setSpeed, 0.05, 0.5, (v) => `${v.toFixed(2)} Hz`)}
        {slider("8D Movement", intensity, setIntensity, 0, 1, describe8DMovement)}
        {slider("Bass boost", bass, setBass, 0, 1, (v) => `${Math.round(v * 100)}%`)}
        {slider("Reverb", reverb, setReverb, 0, 1, (v) => `${Math.round(v * 100)}%`)}
      </div>
      <p className="text-[11px] text-white/55 mt-3">
        Changed a slider? Re-drop the file to render with the new settings.
      </p>

      {error && (
        <p role="alert" className="mt-4 text-sm text-[#FF4444]">
          {error}
        </p>
      )}

      {/* Result */}
      {outUrl && (
        <div className="mt-6 rounded-xl border border-[#2EC4B6]/30 bg-[#2EC4B6]/[0.06] p-4">
          <div className="text-sm font-bold text-[#2EC4B6] mb-3">
            Done — put your headphones on 🎧
          </div>
          <audio controls src={outUrl} className="w-full" preload="metadata" />
          <div className="flex flex-wrap gap-3 mt-4">
            <a
              href={outUrl}
              download={outName}
              title="Download converted 8D audio as WAV"
              className="px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[1.4px] text-white bg-gradient-to-r from-[#FF3366] to-[#FF9F1C]"
            >
              Download WAV
            </a>
            <a
              href="/?utm_source=converter&utm_medium=tool_result#pricing"
              title="Install the free 8Dfy browser extension"
              className="px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[1.4px] text-white/70 border border-white/20 hover:text-white hover:border-white/40 transition-colors"
            >
              Want this live on YouTube or Spotify? Get the free extension →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
